// Self-hosted brand faces (@fontsource): no request leaves our origin on a visit and
// nothing depends on Google Fonts. Only the weights the token scale uses are loaded —
// Big Shoulders Display for headings and every number, Public Sans for the rest.
// Imported without the .css suffix: the packages' exports map is `./*` → `./*.css`,
// so the suffixed specifier resolves to `400.css.css` and fails.
import '@fontsource/big-shoulders-display/400';
import '@fontsource/big-shoulders-display/500';
import '@fontsource/big-shoulders-display/700';
import '@fontsource/public-sans/400';
import '@fontsource/public-sans/500';
import '@fontsource/public-sans/600';
import './theme/tokens.generated.css';
import './normalize.css';
import './i18n';
import 'swiper/css';
import 'swiper/css/pagination';

import type { ReactNode } from 'react';
import type { LoaderFunctionArgs } from 'react-router';

import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { ThemeProvider } from 'styled-components';
import { useRef, useState, useEffect } from 'react';
import { init, postEvent } from '@telegram-apps/sdk-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
  Links,
  Outlet,
  Scripts,
  useMatches,
  useLocation,
  useRouteError,
  PrefetchPageLinks,
  useRouteLoaderData,
} from 'react-router';

import { isWideRequest } from 'src/utils/device';
import { isTelegramWebApp } from 'src/utils/telegram';
import { SPLASH_LIMIT_MS } from 'src/utils/requestError';
import { dealerProfileUrl } from 'src/utils/dealerProfile';
import { DEFAULT_DOCUMENT_TITLE } from 'src/utils/documentTitle';

import { CONFIG } from 'src/config-global';
import { QUERY_DEFAULTS } from 'src/queries/clientDefaults';

import { defaultTheme } from './theme';
import { Loading } from './screens/Loading';
import { TabBar } from './components/TabBar';
import { BlockchainProvider } from './blockchain';
import { Stack } from './components/themed/Stack';
import { useHydrated } from './hooks/useHydrated';
import { DocumentHead } from './components/DocumentHead';
import { DocumentTitleHost } from './hooks/useDocumentTitle';
import { RequestFailureState } from './components/EmptyState';
import { useSession, SessionProvider } from './hooks/useSession';
import { navPlacement } from './components/TabBar/useNavPlacement';
import { AssistantConversationsProvider } from './components/AssistantPanel';
import { WIDE_QUERY, useMediaQuery, WideHintProvider } from './hooks/useMediaQuery';

dayjs.extend(duration);

// The browser's half of the Telegram SDK: `isTelegramWebApp()` is false on the server.
if (isTelegramWebApp()) {
  try {
    init();
  } catch (e) {
    console.warn('Telegram SDK init failed:', e);
  }
}

/**
 * What the server tells every page before it renders: whether the visitor's device is a phone.
 * The layout follows the window's width, which only the browser knows; the server takes the
 * device's own word for it, so a laptop gets the laptop's layout in the first markup already
 * (`useIsWide`). The browser's real width takes over as soon as it has the page.
 */
export function loader({ request }: LoaderFunctionArgs) {
  return { wide: isWideRequest(request) };
}

/** The device does not change during a visit: the hint is asked once, with the document. */
export const shouldRevalidate = () => false;

/** A route says so when the server renders its screen for everyone; the rest stay behind the splash. */
export interface RouteHandle {
  serverRendered?: boolean;
}

/**
 * The document. The stylesheets of the routes on screen come through `<Links />`; the styles of
 * the styled components are put into `<head>` by `entry.server.tsx`.
 */
export function Layout({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const hint = useRouteLoaderData<typeof loader>('root');
  // Where the tab bar sits, for the rule that starts a screen under a top bar (`navPlacement`).
  const nav = navPlacement(pathname, useMediaQuery(WIDE_QUERY, hint?.wide ?? false));

  return (
    <html lang="en" data-nav={nav}>
      <head>
        <meta charSet="UTF-8" />
        {/* Tab icon: the GAC mark on the brand's black field, built from the single source
            design/brandbook/symbol.svg by design/brandbook/build-icons.py. */}
        <link rel="icon" type="image/svg+xml" href="/favicon.svg" />
        <link rel="alternate icon" href="/favicon.ico" sizes="16x16 32x32 48x48" />
        <link rel="apple-touch-icon" href="/apple-touch-icon.png" />
        {/* No maximum-scale, no user-scalable=no: a buyer who finds the text small must be able to
            pinch-zoom (BUG-11). Telegram's mini-app container manages its own viewport on top. */}
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Links />
      </head>
      <body>
        <div id="root">{children}</div>
        <Scripts />
      </body>
    </html>
  );
}

export default function Root() {
  // One QueryClient for the app: the browser keeps it for the whole visit, the server makes one
  // for every page it renders. `EvmProvider` reuses it rather than mounting a second one (app#201),
  // so anything set here really is the rule for every query on the storefront.
  const [queryClient] = useState(() => new QueryClient({ defaultOptions: { queries: QUERY_DEFAULTS } }));
  const hint = useRouteLoaderData<typeof loader>('root');

  return (
    <QueryClientProvider client={queryClient}>
      <BlockchainProvider>
        <ThemeProvider theme={defaultTheme}>
          <WideHintProvider wide={hint?.wide ?? false}>
            <SessionProvider>
              {/* The assistant's conversations live above the routes: a car's page and Back keep them (app#397). */}
              <AssistantConversationsProvider>
                <Shell />
              </AssistantConversationsProvider>
            </SessionProvider>
          </WideHintProvider>
        </ThemeProvider>
      </BlockchainProvider>
    </QueryClientProvider>
  );
}

/** One address under every route of the table (`routes.ts`) — which route, not which car. */
const A_PAGE_OF_EVERY_ROUTE = ['/buy', '/rent', '/listing/-', '/rentals/listing/-', '/dealer/-', '/deals'];

/**
 * The rest of the app, fetched as soon as the browser has the page.
 *
 * The first page arrives with the code of its own route only — that is what makes it quick. The
 * storefront used to be one bundle, though, and a person who lost the connection after it loaded
 * could still open any screen and be told "No connection" there (SF-H3). So the other routes'
 * code follows right behind the first paint: by the time anybody taps a tab, it is in. Their
 * stylesheets follow the same way, from the document itself (`entry.server.tsx`).
 */
const RestOfTheApp = () =>
  useHydrated() ? (
    <>
      {A_PAGE_OF_EVERY_ROUTE.map((page) => (
        <PrefetchPageLinks key={page} page={page} />
      ))}
    </>
  ) : null;

/**
 * What every screen stands in: the splash while the session is being checked, then the screen —
 * inside the page's one `main` — and the tab bar beside it.
 *
 * The splash is for the screens only a session opens. A screen the server renders — a catalog, a
 * car, a dealer's page — is on the visitor's screen before any script has run, with what everyone
 * may see; putting the splash over it would take the cars away to check a session they do not
 * depend on.
 */
function Shell() {
  const { isLoading, isLoggedIn, account } = useSession();
  // A dealer's Profile tab is their profile in the dealer cabinet (app#370). The account is the
  // browser's to know: on the server, and in the render that takes its markup over, there is none,
  // and the tab is the storefront's own until `/user/auth/me` has named the role.
  const dealerProfile = dealerProfileUrl(account?.role, CONFIG.dealerCabinetUrl);
  // A page the server renders for everyone: a route that says so, or an address with nothing at it.
  const serverRendered = useMatches().some(
    (match) =>
      (match.handle as RouteHandle | undefined)?.serverRendered ||
      (match.loaderData as { notFound?: boolean } | undefined)?.notFound
  );
  const [progress, setProgress] = useState(0);
  const progressTimer = useRef<ReturnType<typeof setInterval>>(undefined);
  // The splash has a limit (spec PR-4 / BUG-05): past it, a bar that never ends becomes a screen
  // that says so. `useSession().isLoading` is `/user/auth/me` in flight — a wedged api held the
  // whole app behind the bar for as long as the tab stayed open.
  const [splashStalled, setSplashStalled] = useState(false);

  useEffect(() => {
    if (isTelegramWebApp()) {
      postEvent('web_app_set_background_color', { color: '#000' });
      postEvent('web_app_set_header_color', { color: '#000' });
      postEvent('web_app_setup_closing_behavior', { need_confirmation: true });
      postEvent('web_app_setup_swipe_behavior', { allow_vertical_swipe: false });
      postEvent('web_app_expand');
    }
    progressTimer.current = setInterval(() => {
      setProgress((prev) => Math.min(prev + Math.random() * 20, 90));
    }, 100);

    return () => clearInterval(progressTimer.current);
  }, []);

  // The document says when the browser has taken the page over. Until then a server-rendered page
  // is on screen with its links working — they are plain addresses — and its buttons not yet:
  // whatever has to tap one (the storefront's own checks first of all) waits for this.
  useEffect(() => {
    document.documentElement.dataset.hydrated = 'true';
  }, []);

  // A full bar has nothing left to animate, and the shell never unmounts: without this the timer
  // ran for the whole life of the tab (app#211).
  useEffect(() => {
    if (progress >= 90) clearInterval(progressTimer.current);
  }, [progress]);

  const showSplash = !serverRendered && (isLoading || progress < 90);

  // The limit counts from the moment the splash is up and is forgotten when it goes away: the
  // splash returns briefly after a sign-in (`/user/auth/me` becomes enabled), and a latch set
  // once at mount would greet that with "not answering" while the api is answering.
  useEffect(() => {
    if (!showSplash) return undefined;
    const limit = setTimeout(() => setSplashStalled(true), SPLASH_LIMIT_MS);

    return () => {
      clearTimeout(limit);
      setSplashStalled(false);
    };
  }, [showSplash]);

  if (showSplash) {
    return (
      <>
        {/* No screen is up to name the tab yet. */}
        <DocumentHead title={DEFAULT_DOCUMENT_TITLE} bare />
        <RestOfTheApp />
        <Loading progress={progress} stalled={splashStalled} onRetry={() => window.location.reload()} />
      </>
    );
  }

  return (
    <Stack>
      <RestOfTheApp />
      {/* Where a screen's content starts, for a screen reader's "skip to main" (app#242). Every
          route renders inside it and the tab bar outside: it is not the content of any screen.
          Layout does not change — each screen's `.container` sets its own height and scroll. */}
      <main>
        <DocumentTitleHost>
          <Outlet />
        </DocumentTitleHost>
      </main>
      <TabBar isLoggedIn={isLoggedIn} profileTo={dealerProfile ?? undefined} />
    </Stack>
  );
}

/**
 * What is shown when a screen cannot be rendered at all — a failure in the code, on the server or
 * in the browser. It used to be a blank page; now it says so and offers the way out that works
 * for anything transient. It stands outside the providers on purpose: whatever failed may be one
 * of them.
 */
export function ErrorBoundary() {
  const error = useRouteError();

  useEffect(() => {
    console.error(error);
  }, [error]);

  return (
    <ThemeProvider theme={defaultTheme}>
      <DocumentHead title="Something went wrong" />
      <main>
        <div className="container">
          <Stack width="100%" p={4} pb={30} maxWidth="750px">
            <RequestFailureState titleAs="h1" kind="failed" onRetry={() => window.location.reload()} />
          </Stack>
        </div>
      </main>
    </ThemeProvider>
  );
}
